import { useMemo, useState } from 'react';
import { Incident } from '../data/demoData';
import { apiFetch } from '../lib/apiClient';

interface Props {
  incident: Incident;
  customerId: string;
  onRunKql?: (query: string) => void;
}

interface GuidedTriageStep {
  id: string;
  title: string;
  objective: string;
  status: 'pending' | 'review' | 'complete';
  verdict: 'Pass' | 'Needs Review' | 'Risk Found' | 'Unknown';
  evidence: string[];
  recommendedQueries: Array<{ label: string; source: 'sentinel' | 'defender'; query: string }>;
  nextAction: string;
}

interface GuidedTriageResult {
  ok: true;
  incidentId: string;
  customerId: string;
  generatedAt: string;
  confidence: 'Low' | 'Medium' | 'High';
  recommendedClassification: string;
  recommendedClosureReason: string;
  steps: GuidedTriageStep[];
  customerComment: string;
  aiSummary: { usedAi: boolean; model: string; markdown: string };
  diagnostics: string[];
}

function getIncidentId(incident: Incident): string {
  return String((incident as unknown as Record<string, unknown>).armIncidentName || incident.id || incident.incidentNumber || '').trim();
}

function verdictClass(verdict: string): string {
  if (verdict === 'Pass') return 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300';
  if (verdict === 'Risk Found') return 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300';
  if (verdict === 'Needs Review') return 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300';
  return 'bg-muted text-muted-foreground';
}

export function GuidedTriagePanel({ incident, customerId, onRunKql }: Props) {
  const incidentId = useMemo(() => getIncidentId(incident), [incident]);
  const [result, setResult] = useState<GuidedTriageResult | null>(null);
  const [localStatus, setLocalStatus] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function generate() {
    setLoading(true); setError(null); setCopied(false);
    try {
      const data = await apiFetch<GuidedTriageResult>(`/incidents/${encodeURIComponent(incidentId)}/guided-triage`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, incident }),
      });
      setResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function updateStep(stepId: string, status: 'pending' | 'review' | 'complete') {
    setLocalStatus((prev) => ({ ...prev, [stepId]: status }));
    try {
      await apiFetch(`/incidents/${encodeURIComponent(incidentId)}/guided-triage/action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, stepId, status }),
      });
    } catch {
      // Keep UI responsive. Audit failures are visible server-side; next regenerate will restore deterministic state.
    }
  }

  async function copyComment() {
    if (!result) return;
    await navigator.clipboard.writeText(result.customerComment);
    setCopied(true);
  }

  return (
    <section className="mt-3 rounded-xl border border-blue-200 dark:border-blue-900 bg-blue-50/60 dark:bg-blue-950/20 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-blue-900 dark:text-blue-100">Analyst Workbench / Guided Triage</p>
          <p className="text-xs text-blue-800/80 dark:text-blue-200/80">A repeatable triage wizard for source validation, reputation, historical activity, TI, vulnerability context, classification and customer communication.</p>
        </div>
        <button onClick={generate} disabled={loading} className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-60">{loading ? 'Building…' : result ? 'Regenerate Workbench' : 'Start Guided Triage'}</button>
      </div>
      {error && <div className="mt-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-2 text-xs text-red-700 dark:text-red-200">{error}</div>}
      {result && (
        <div className="mt-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">Classification</p><p className="text-sm font-bold">{result.recommendedClassification}</p></div>
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">Confidence</p><p className="text-sm font-bold">{result.confidence}</p></div>
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">AI path</p><p className="text-sm font-bold">{result.aiSummary.usedAi ? 'Azure OpenAI' : 'Fallback'}</p></div>
          </div>
          <div className="rounded-lg border border-border bg-card p-3">
            <p className="text-xs font-bold">Recommended closure / disposition reason</p>
            <p className="mt-1 text-xs text-muted-foreground">{result.recommendedClosureReason}</p>
          </div>
          <div className="space-y-3">
            {result.steps.map((step, idx) => {
              const status = (localStatus[step.id] || step.status) as GuidedTriageStep['status'];
              return (
                <div key={step.id} className="rounded-xl border border-border bg-card p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-bold text-foreground">{idx + 1}. {step.title}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{step.objective}</p>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-[11px] font-bold ${verdictClass(step.verdict)}`}>{step.verdict}</span>
                  </div>
                  <ul className="mt-2 list-disc pl-5 text-xs text-muted-foreground">
                    {step.evidence.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                  <p className="mt-2 text-xs"><span className="font-semibold">Next action:</span> {step.nextAction}</p>
                  {step.recommendedQueries.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {step.recommendedQueries.map((q) => (
                        <button key={`${step.id}-${q.label}`} onClick={() => onRunKql?.(q.query)} className="rounded-lg border border-border px-2.5 py-1.5 text-[11px] font-semibold hover:bg-muted">Run pivot: {q.label}</button>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(['pending', 'review', 'complete'] as const).map((s) => (
                      <button key={s} onClick={() => void updateStep(step.id, s)} className={`rounded-lg px-2.5 py-1.5 text-[11px] font-semibold ${status === s ? 'bg-blue-600 text-white' : 'border border-border hover:bg-muted'}`}>{s}</button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="rounded-lg border border-border bg-card p-3">
            <div className="flex items-center justify-between gap-2"><p className="text-xs font-bold">Generated customer comment</p><button onClick={copyComment} className="rounded-lg border border-border px-2.5 py-1.5 text-[11px] font-semibold hover:bg-muted">{copied ? 'Copied' : 'Copy comment'}</button></div>
            <p className="mt-2 text-xs text-muted-foreground">{result.customerComment}</p>
          </div>
          {result.diagnostics.length > 0 && <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs text-amber-800 dark:text-amber-200">{result.diagnostics.join(' ')}</div>}
        </div>
      )}
    </section>
  );
}
